import React, { useState, useEffect, useCallback } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { EllipsisVerticalIcon, PlusIcon } from '@heroicons/react/20/solid'
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useParams } from 'react-router-dom';
import HeadingCp from '../components/HeadingCp';
import Modal from '../components/Modal';
import ShortDateFormat from '../components/ShortDateFormat';
import PhoneNumber from '../components/PhoneNumber';
import LoadingSpinner from '../components/LoadingSpinner';
import Accordion from '../components/Accordion';
import { FlagIcon, TrashIcon } from '@heroicons/react/24/outline';
import SelectParts from '../components/SelectParts';
import { set } from 'date-fns';


const TechWipDetails = () => {
  const navigate = useNavigate();
  const [job, setJob] = useState([]);
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;
  const [isNoCharge, setIsNoCharge] = useState(false);
  const [techNotes, setTechNotes] = useState('');
  const [openIndex, setOpenIndex] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [serviceEmail, setServiceEmail] = useState({
    customerEmail: '',
    customerPrint: '',
    noEmail: false
  });
  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  useEffect(() => {
    setLoading(true);
    fetchJob(); //service call
   }, [apiUrl, navigate, id]);

  useEffect(() => {
    if (job?.TYPE) {
      setIsNoCharge(job.TYPE === 'NO CHARGE');
    }
  }, [job]);
 
  const fetchJob = async () => { 
    try {
      const response = await axios.get(`${apiUrl}/wip_crud.php?getWipTechDetail=${id}`, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
      });
      if (response.data[0] === 'success') {
        const jobData = response.data[1] ?? [];
        setJob(jobData);
        const addTechNotes = jobData.tech_notes || ''; // Ensure TECHNOTES is set to an empty string if it's not available
        if (addTechNotes.length > 0) {
          setIsReady(true);
        }
        setTechNotes(addTechNotes);
        
      } else {
        toast.error(response.data.error, { position: "top-center" });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session expired. Please login again.", { position: "top-center" });
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops!! you don't have access to this area. Speak to the CFO.", { position: "top-center" });
      }
    } finally {
      setLoading(false);
    }
  };
  const handleTechNotesChange = (e) => {
    setTechNotes(e.target.value); // Correctly update the state with the input value
  };
  const handleSubmitTechNotes = async () => {
    setLoading(true);

    const bodyData = {
      status: "addTechNotes", // Assuming this is the correct status for your backend
      tech_notes: techNotes,  // Send the updated tech notes
      schedule_id: id,  // Assuming id is the service call ID
    };
    try {
      const response = await axios.post(`${apiUrl}/wip_crud.php`, bodyData, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.data[0] === 'success') {
        setIsReady(true);
        setJob((prevState) => ({ ...prevState, tech_notes: techNotes, }));
        toast.success('Tech notes updated!', { position: 'top-center' });
      } else {
        toast.info('Failed to update tech notes', { position: 'top-center' });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops! You don't have access to this area. Speak to the CFO.", { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }
  };
  const handleSubmitComplete = async () => {
    setLoading(true);

    const bodyData = {
      status: "wipCompleted", // Assuming this is the correct status for your backend
      schedule_id: id,  // Assuming id is the service call ID
    };
    try {
      const response = await axios.post(`${apiUrl}/wip_crud.php`, bodyData, {
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.data[0] === 'success') {
        toast.success('WIP completed successfully!', { position: 'top-center' });
        navigate('/t/twiplist'); // Redirect to the WIP list after successful completion
      } else {
        toast.info('Failed to update tech notes', { position: 'top-center' });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops! You don't have access to this area. Speak to the CFO.", { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }
  };
  const handleFlag = async () => {
    setLoading(true);

    const bodyData = {
      status: "wipFlagged", // Assuming this is the correct status for your backend
      schedule_id: id,  // Assuming id is the service call ID
    };
    try {
      const response = await axios.post(`${apiUrl}/wip_crud.php`, bodyData, {
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.data[0] === 'success') {
        toast.success('WIP flagged successfully!', { position: 'top-center' });
        navigate('/t/twiplist'); // Redirect to the WIP list after successful completion
      } else {
        toast.info('Failed to update tech notes', { position: 'top-center' });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops! You don't have access to this area. Speak to the CFO.", { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='pb-10'>
    <HeadingCp label={"Job Details"}/>

    <ul role="list" className="divide-y divide-gray-100 p-3">
      {job && (
        <div className="grid grid-cols-1 sm:grid-cols-2">
          <div className="flex items-center text-sm gap-1">
            <p>{job.schedule_from ? <ShortDateFormat date={job.schedule_from} /> :job.schedule_from }</p>
            <p>-</p>
            <p>{job.schedule_to ? <ShortDateFormat date={job.schedule_to} /> :job.schedule_to }</p>
          </div>
          <div className="flex items-center text-sm gap-1">
            <p>{job.start_time ? job.start_time : ''}</p>
            <p>{job.end_time ? "@ " + job.end_time : ''}</p>
          </div>
          <div key={job.schedule_id} className="relative flex items-center">
            <div className="min-w-0 flex-1">
              <span aria-hidden="true" className="absolute inset-0" />
              <p className="text-sm font-medium text-green-700">Job# {job.job_number} | {job.job_site}</p>
              <p className="truncate text-sm text-gray-500">{job.job_description}</p>
              <p className="truncate text-xs text-red-700">Type: {job.type}</p>
              <p className="truncate text-xs text-gray-700">Coordinator comment: {job.user_comment}</p>
            </div>
          </div>
        </div>
      )}
    </ul>

      <div>Tech's Notes</div>
      {/* Tech Notes */}
      <div className='mb-3'>
        <textarea
          value={techNotes || ''}
          onChange={handleTechNotesChange}
          className="w-full border border-gray-300 rounded-md px-4 py-2 mb-3"
          rows="4"
        />
        <div className='gap-2 flex justify-end'>
        <button onClick={handleSubmitTechNotes} className="px-4 py-2 bg-blue-500 text-white rounded">
          Add Notes
        </button>
        {isReady && (
          <>
          <button onClick={handleFlag} className="px-4 py-2 bg-red-600 text-white rounded">
          <FlagIcon className="h-5 w-5" aria-hidden="true" />
          </button>          
         <button onClick={handleSubmitComplete} className="px-4 py-2 bg-orange-500 text-white rounded">
          Complete
          </button>
          </>
        )}
      </div>
      </div>
    </div>
  );
};

export default TechWipDetails;
